import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/nova/components';
import { COLORS } from '@/nova/theme/tokens';
import type { ShowcaseHighlight as ShowcaseHighlightData } from '@/nova/mock-data/types';
import { useShowcaseReveal } from './useShowcaseReveal';
import {
  Body,
  Card,
  Control,
  Description,
  Header,
  IconWrap,
  Inner,
  MinusButton,
  RING_R,
  RING_SIZE,
  RingProgress,
  RingSvg,
  RingTrack,
  Title,
} from './ShowcaseHighlight.styles';

export type ShowcaseHighlightProps = {
  highlight: ShowcaseHighlightData;
  active: boolean;
  /** Reveal instantly with no animation (already-seen cards). */
  instant: boolean;
  /** When set (and active), the card shows a depleting ring counting down this dwell
   *  to the next talking point. When undefined (and active), it shows a collapse button. */
  countdownMs?: number;
  onClick: () => void;
  /** Collapse the (active) card via the corner button — shown only when not autoplaying. */
  onCollapse?: () => void;
  /** Called when the reveal (or instant populate) for this card finishes. */
  onRevealComplete?: () => void;
};

/** A small depleting ring that empties over `durationMs` (the autoplay dwell). */
const CountdownRing = ({ durationMs }: { durationMs: number }): React.ReactElement => {
  const centre = RING_SIZE / 2;
  return (
    <RingSvg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} aria-hidden>
      <RingTrack cx={centre} cy={centre} r={RING_R} />
      <RingProgress cx={centre} cy={centre} r={RING_R} $durationMs={durationMs} />
    </RingSvg>
  );
};

/**
 * One talking point in the showcase stack: a collapsed pill (title) that morphs
 * into an expanded card revealing a char-by-char-typed description. Width/height
 * are measured imperatively to fit the title when collapsed and fill the column
 * when active — the configurator highlight card's pill-fit behaviour, simplified
 * for feature-tier highlights (no source pills). While expanded it shows, top-right,
 * either a depleting autoplay countdown ring or a collapse button.
 */
export const ShowcaseHighlight = ({
  highlight,
  active,
  instant,
  countdownMs,
  onClick,
  onCollapse,
  onRevealComplete,
}: ShowcaseHighlightProps): React.ReactElement => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // Latest `active`, read inside the deferred fonts-ready callback without re-binding it.
  const activeRef = useRef(active);
  activeRef.current = active;

  const text = useShowcaseReveal(highlight, active, instant, onRevealComplete);

  // Size the collapsed pill to fit its title. The header is measured UNCAPPED (its
  // `max-width: 100%` momentarily lifted) so we read the title's full width even when it
  // is currently ellipsized — otherwise each re-measure would read the shrunken width and
  // ratchet the pill smaller (a spiral that cut titles with plenty of room). The width is
  // still clamped to the column, so a genuinely too-long title ellipsizes inside the cap.
  const sizeCollapsed = useCallback(() => {
    const card = cardRef.current;
    const header = headerRef.current;
    if (!card || !header) return;
    const capped = header.style.maxWidth;
    header.style.maxWidth = 'none';
    const full = header.offsetWidth;
    header.style.maxWidth = capped;
    const max = card.parentElement?.clientWidth ?? Infinity;
    // +28 ≈ inner padding (20) + border (2) + a little slack so it never sits exactly
    // on the edge (sub-pixel rounding would otherwise trip the ellipsis).
    card.style.width = `${Math.min(full + 28, max)}px`;
    card.style.height = '40px';
  }, []);

  // Activate on Enter/Space (the card is a role=button), so keyboard users can drive
  // the showcase, not just the pointer.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  // The collapse button lives inside the clickable card, so swallow the click (it
  // collapses, it must not re-trigger the card's own toggle).
  const onCollapseClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onCollapse?.();
    },
    [onCollapse],
  );

  // Re-measure the collapsed width once fonts load (their metrics shift the title width).
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready
      .then(() => {
        if (!cancelled && !activeRef.current) sizeCollapsed();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sizeCollapsed]);

  // Size the card on mount + every active change: collapsed → fit title; active → fill
  // the column, height tracking content.
  useEffect(() => {
    const card = cardRef.current;
    const inner = innerRef.current;
    if (!card || !inner) return;

    if (!active) {
      sizeCollapsed();
      return;
    }

    // Match the expanded card to its column width and KEEP it matched as the layout
    // settles / on resize — a one-shot read could catch a transient width and leave the
    // card overflowing into the video column.
    const parent = card.parentElement;
    let lastWidth = -1;
    const fillWidth = (): void => {
      const width = parent?.clientWidth ?? 320;
      if (width === lastWidth) return;
      lastWidth = width;
      card.style.width = `${width}px`;
    };
    fillWidth();
    const widthRO = parent ? new ResizeObserver(fillWidth) : null;
    if (parent && widthRO) widthRO.observe(parent);
    const heightRO = new ResizeObserver(() => {
      card.style.height = `${Math.max(inner.offsetHeight, 40)}px`;
    });
    heightRO.observe(inner);
    return () => {
      widthRO?.disconnect();
      heightRO.disconnect();
    };
  }, [active, sizeCollapsed]);

  return (
    <Card
      ref={cardRef}
      $active={active}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      // The collapsed pill may show only an icon (a highlight with an empty title), so
      // give the card an accessible name from its own content.
      aria-label={highlight.title || highlight.text}
    >
      <Inner ref={innerRef} $active={active}>
        <Header ref={headerRef} $active={active}>
          {active ? (
            // Leading slot when expanded: the countdown ring (autoplaying) or the
            // collapse button — in the same spot the "+" occupies when collapsed.
            <Control>
              {countdownMs != null ? (
                <CountdownRing durationMs={countdownMs} />
              ) : (
                <MinusButton type="button" onClick={onCollapseClick} aria-label={t('Collapse')}>
                  <Icon name="minus" size={14} color={COLORS.textSubtle} />
                </MinusButton>
              )}
            </Control>
          ) : (
            <IconWrap>
              <Icon name="plus" size={14} color={COLORS.textSubtle} />
            </IconWrap>
          )}
          <Title text={highlight.title} $active={active} />
        </Header>
        <Body $active={active}>
          <Description text={text} />
        </Body>
      </Inner>
    </Card>
  );
};

export default ShowcaseHighlight;
